import type { IPeriodFilter, MonthKey } from '../types/period';
import type {
  IReportComponentDefinition,
  IReportComponentSize,
  IReportItem,
  IReportSavedView,
  ReportComponentId,
} from '../types/report';

interface IDefaultPlacement extends Partial<IReportComponentSize> {
  id: ReportComponentId;
  x: number;
  y: number;
}

const TILE_SIZE = { defaultSize: { w: 6, h: 4 }, minSize: { w: 3, h: 3 } };
const CHART_SIZE = { defaultSize: { w: 6, h: 4 }, minSize: { w: 3, h: 3 } };
const TABLE_SIZE = { defaultSize: { w: 6, h: 5 }, minSize: { w: 4, h: 3 } };
const COLUMN_SIZE = { defaultSize: { w: 4, h: 5 }, minSize: { w: 3, h: 4 } };
const LIST_SIZE = { defaultSize: { w: 6, h: 4 }, minSize: { w: 3, h: 3 } };

/** Catalogue of report components plus the seeded default layouts. */
export class ReportComponentHelper {
  static readonly DEFAULT_VIEW_ID = 'default';

  static readonly COLUMN_VIEW_ID = 'columns';

  static readonly GRID_COLS = 12;

  static readonly ROW_HEIGHT = 72;

  static readonly REGISTRY: IReportComponentDefinition[] = [
    {
      id: 'revenue_tile',
      title: 'Revenue',
      description: 'Value and delta vs comparison',
      note: 'Operating revenue, excludes other income',
      kind: 'tile',
      ...TILE_SIZE,
    },
    {
      id: 'gross_profit_tile',
      title: 'Gross profit',
      description: 'Gross profit and gross margin %',
      note: 'Revenue minus COGS',
      kind: 'tile',
      ...TILE_SIZE,
    },
    {
      id: 'ebitda_tile',
      title: 'EBITDA',
      description: 'EBITDA and EBITDA margin %',
      note: 'Operating profit plus depreciation and amortization',
      kind: 'tile',
      ...TILE_SIZE,
    },
    {
      id: 'net_profit_tile',
      title: 'Net profit',
      description: 'Net profit and net margin %',
      note: 'After tax',
      kind: 'tile',
      ...TILE_SIZE,
    },
    {
      id: 'opex_tile',
      title: 'Total opex',
      description: 'Operating expenses and delta',
      note: 'Operating expenses, excludes COGS',
      kind: 'tile',
      ...TILE_SIZE,
    },
    {
      id: 'cash_tile',
      title: 'Cash + investments',
      description: 'Closing cash and short-term investments',
      note: 'Closing cash and short-term investments',
      kind: 'tile',
      ...TILE_SIZE,
    },
    {
      id: 'net_burn_tile',
      title: 'Net burn',
      description: 'Cash consumed by operations and investing',
      note: 'Cash used by operations and investing',
      kind: 'tile',
      ...TILE_SIZE,
    },
    {
      id: 'runway_tile',
      title: 'Runway',
      description: 'Cash ÷ trailing 3-month average burn',
      note: 'Cash divided by trailing 3-month average burn',
      kind: 'tile',
      ...TILE_SIZE,
    },
    {
      id: 'pnl_summary_table',
      title: 'P&L summary',
      description: 'Revenue to net profit with comparison and margins',
      note: 'Revenue to net profit with margins',
      kind: 'table',
      ...TABLE_SIZE,
    },
    {
      id: 'revenue_trend_chart',
      title: 'Revenue trend',
      description: 'Monthly revenue bars with 3-month moving average',
      note: 'Operating revenue with 3-month average',
      kind: 'chart',
      ...CHART_SIZE,
    },
    {
      id: 'profit_trend_chart',
      title: 'Income vs expenses',
      description: 'Monthly revenue and expenses with the net profit line',
      note: 'Expenses include COGS, opex, other costs and tax',
      kind: 'chart',
      ...CHART_SIZE,
    },
    {
      id: 'revenue_mix_chart',
      title: 'Revenue mix',
      description: 'Stacked revenue by ledger line',
      note: 'Revenue by ledger line',
      kind: 'chart',
      ...CHART_SIZE,
    },
    {
      id: 'margin_trend_chart',
      title: 'Margin trend',
      description: 'Gross, EBITDA and net margin over time',
      note: 'Share of revenue',
      kind: 'chart',
      ...CHART_SIZE,
    },
    {
      id: 'opex_breakdown_chart',
      title: 'Opex breakdown',
      description: 'Stacked opex by reporting bucket',
      note: 'Grouped by reporting bucket',
      kind: 'chart',
      ...CHART_SIZE,
    },
    {
      id: 'top_expenses_table',
      title: 'Top 15 expense lines',
      description: 'Largest expense ledger lines with delta',
      note: 'Largest expense lines by current spend',
      kind: 'table',
      ...TABLE_SIZE,
    },
    {
      id: 'payroll_trend_chart',
      title: 'Payroll vs non-payroll',
      description: 'Payroll and non-payroll opex trend',
      note: 'Opex split into payroll and other',
      kind: 'chart',
      ...CHART_SIZE,
    },
    {
      id: 'variance_table',
      title: 'Variance flagger',
      description: 'Largest movers vs comparison window',
      note: 'Largest movers vs the comparison window',
      kind: 'table',
      ...TABLE_SIZE,
    },
    {
      id: 'cash_bridge_chart',
      title: 'Cash bridge',
      description: 'Opening to closing cash waterfall (indirect method)',
      note: 'Indirect method from balance sheet movements',
      kind: 'chart',
      ...CHART_SIZE,
    },
    {
      id: 'burn_runway_chart',
      title: 'Burn & runway trend',
      description: 'Monthly net burn with runway line',
      note: 'Monthly net burn with runway in months',
      kind: 'chart',
      ...CHART_SIZE,
    },
    {
      id: 'balance_sheet_table',
      title: 'Comparative balance sheet',
      description: 'Balance sheet by bucket vs comparison',
      note: 'Balances by bucket vs comparison',
      kind: 'table',
      ...TABLE_SIZE,
    },
    {
      id: 'working_capital_table',
      title: 'Working capital days',
      description: 'DSO, DPO, DIO and cash conversion cycle',
      note: 'DSO, DPO, DIO and cash conversion cycle',
      kind: 'table',
      defaultSize: { w: 6, h: 3 },
      minSize: { w: 3, h: 2 },
    },
    {
      id: 'cash_column',
      title: 'Cash + investments',
      description:
        'Cash headline with net burn, runway and a 12-month sparkline',
      note: 'Closing cash and short-term investments',
      kind: 'column_header',
      columnLayoutOnly: true,
      ...COLUMN_SIZE,
    },
    {
      id: 'profit_column',
      title: 'Net profit',
      description: 'Profit headline with margins and a 12-month sparkline',
      note: 'After tax',
      kind: 'column_header',
      columnLayoutOnly: true,
      ...COLUMN_SIZE,
    },
    {
      id: 'revenue_column',
      title: 'Revenue',
      description:
        'Revenue headline with trailing totals and a 12-month sparkline',
      note: 'Operating revenue, excludes other income',
      kind: 'column_header',
      columnLayoutOnly: true,
      ...COLUMN_SIZE,
    },
    {
      id: 'opex_breakdown_list',
      title: 'Opex breakdown',
      description: 'Ranked operating expense buckets with share and change',
      note: 'Grouped by reporting bucket',
      kind: 'breakdown_list',
      columnLayoutOnly: true,
      ...LIST_SIZE,
    },
    {
      id: 'revenue_breakdown_list',
      title: 'Revenue breakdown',
      description: 'Ranked revenue lines with share and change',
      note: 'Revenue by ledger line',
      kind: 'breakdown_list',
      columnLayoutOnly: true,
      ...LIST_SIZE,
    },
  ];

  private static readonly BY_ID = new Map(
    ReportComponentHelper.REGISTRY.map((definition) => [
      definition.id,
      definition,
    ])
  );

  /** Headline tiles, the six charts most accounting tools lead management reporting with, then the P&L. */
  private static readonly CLASSIC_LAYOUT: IDefaultPlacement[] = [
    { id: 'revenue_tile', x: 0, y: 0 },
    { id: 'gross_profit_tile', x: 6, y: 0 },
    { id: 'net_profit_tile', x: 0, y: 4 },
    { id: 'cash_tile', x: 6, y: 4 },
    { id: 'revenue_trend_chart', x: 0, y: 8 },
    { id: 'profit_trend_chart', x: 6, y: 8 },
    { id: 'margin_trend_chart', x: 0, y: 12 },
    { id: 'opex_breakdown_chart', x: 6, y: 12 },
    { id: 'cash_bridge_chart', x: 0, y: 16 },
    { id: 'burn_runway_chart', x: 6, y: 16 },
    { id: 'pnl_summary_table', x: 0, y: 20, w: 12, h: 6 },
  ];

  /** Three headline columns, four trend charts plus the opex list, then the P&L. */
  private static readonly COLUMN_LAYOUT: IDefaultPlacement[] = [
    { id: 'cash_column', x: 0, y: 0 },
    { id: 'profit_column', x: 4, y: 0 },
    { id: 'revenue_column', x: 8, y: 0 },
    { id: 'revenue_trend_chart', x: 0, y: 5 },
    { id: 'profit_trend_chart', x: 6, y: 5 },
    { id: 'margin_trend_chart', x: 0, y: 9 },
    { id: 'opex_breakdown_list', x: 6, y: 9 },
    { id: 'cash_bridge_chart', x: 0, y: 13 },
    { id: 'burn_runway_chart', x: 6, y: 13 },
    { id: 'pnl_summary_table', x: 0, y: 17, w: 12, h: 6 },
  ];

  static definition(id: ReportComponentId): IReportComponentDefinition {
    const definition = ReportComponentHelper.BY_ID.get(id);
    if (!definition) throw new Error(`Unknown report component: ${id}`);
    return definition;
  }

  static isComponentId(value: string): value is ReportComponentId {
    return ReportComponentHelper.REGISTRY.some((d) => d.id === value);
  }

  /** Components offered in the palette and rendered on the canvas for this flag state. */
  static isAvailable(id: ReportComponentId, columnLayout: boolean): boolean {
    return (
      columnLayout || !ReportComponentHelper.definition(id).columnLayoutOnly
    );
  }

  static palette(columnLayout: boolean): IReportComponentDefinition[] {
    return ReportComponentHelper.REGISTRY.filter((d) =>
      ReportComponentHelper.isAvailable(d.id, columnLayout)
    );
  }

  /**
   * Items the canvas renders. Unavailable components are skipped, not removed,
   * so a saved view keeps them for when the flag is on again.
   */
  static visibleItems(
    items: IReportItem[],
    columnLayout: boolean
  ): IReportItem[] {
    return items.filter((item) =>
      ReportComponentHelper.isAvailable(item.componentId, columnLayout)
    );
  }

  static newInstanceId(componentId: ReportComponentId): string {
    const random = Math.random().toString(36).slice(2, 6);
    return `${componentId}-${Date.now().toString(36)}-${random}`;
  }

  static newItem(
    componentId: ReportComponentId,
    position: { x: number; y: number }
  ): IReportItem {
    const { defaultSize } = ReportComponentHelper.definition(componentId);
    return {
      instanceId: ReportComponentHelper.newInstanceId(componentId),
      componentId,
      layout: { ...position, ...defaultSize },
      override: null,
    };
  }

  /** First free row below every placed item, for appending from the palette. */
  static nextRow(items: IReportItem[]): number {
    return items.reduce((max, i) => Math.max(max, i.layout.y + i.layout.h), 0);
  }

  private static placeItems(
    layout: IDefaultPlacement[],
    suffix: string
  ): IReportItem[] {
    return layout.map((placement) => {
      const { defaultSize } = ReportComponentHelper.definition(placement.id);
      return {
        instanceId: `${placement.id}-${suffix}`,
        componentId: placement.id,
        layout: {
          x: placement.x,
          y: placement.y,
          w: placement.w ?? defaultSize.w,
          h: placement.h ?? defaultSize.h,
        },
        override: null,
      };
    });
  }

  static defaultFilter(booksClosedThrough: MonthKey): IPeriodFilter {
    return {
      grain: 'month',
      anchor: booksClosedThrough,
      rangeStart: null,
      comparison: 'prior_period',
    };
  }

  /**
   * Read-only views every report starts with. With the flag off this is
   * exactly the classic layout under the default id; with it on, the default
   * becomes the column layout and the classic layout stays selectable.
   */
  static builtInViews(
    booksClosedThrough: MonthKey,
    columnLayout: boolean
  ): IReportSavedView[] {
    const filter = ReportComponentHelper.defaultFilter(booksClosedThrough);
    const view = (
      id: string,
      name: string,
      layout: IDefaultPlacement[]
    ): IReportSavedView => ({
      id,
      name,
      filter,
      items: ReportComponentHelper.placeItems(layout, id),
      updatedAt: new Date(0).toISOString(),
      isDefault: true,
    });
    const { DEFAULT_VIEW_ID, COLUMN_VIEW_ID, CLASSIC_LAYOUT, COLUMN_LAYOUT } =
      ReportComponentHelper;
    const defaultView = view(DEFAULT_VIEW_ID, 'Default view', CLASSIC_LAYOUT);
    return columnLayout
      ? [defaultView, view(COLUMN_VIEW_ID, 'Column layout', COLUMN_LAYOUT)]
      : [defaultView];
  }
}
